/**
 * DialogsProducao - Dialogs para produção (Diário, Detalhes, Conclusão)
 */
import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { HelpTooltip } from '../ui/help-tooltip';
import { 
  BookOpen, Calendar, Plus, Save, Loader2, 
  Upload, X, Image, Camera, CheckCircle2 
} from 'lucide-react';
import { STATUS_CONFIG, formatDate } from './constantes';

// Dialog do Diário de Produção
export const DialogDiario = ({
  open,
  onClose,
  diarioContent,
  setDiarioContent,
  diarioResponsavel,
  setDiarioResponsavel,
  diarioData,
  setDiarioData,
  diarioAtual,
  currentUser,
  saving,
  onSave
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-amber-600" />
            Diário de Produção
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input 
                type="date" 
                value={diarioData}
                onChange={(e) => setDiarioData(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Responsável</Label>
              <Input 
                value={diarioResponsavel}
                onChange={(e) => setDiarioResponsavel(e.target.value)}
                placeholder={currentUser?.full_name || 'Nome do responsável'}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Registro</Label>
            <Textarea 
              value={diarioContent}
              onChange={(e) => setDiarioContent(e.target.value)}
              placeholder="Descreva o progresso, problemas encontrados, observações..."
              rows={4}
            />
          </div>
          
          {/* Histórico recente */}
          {diarioAtual?.length > 0 && (
            <div className="border-t pt-4">
              <Label className="text-xs text-gray-500 mb-2 block">Últimos Registros</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {diarioAtual.slice(0, 5).map((entry) => (
                  <div key={entry.id} className="p-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <span>{formatDate(entry.data)}</span>
                      <span>•</span>
                      <span className="font-medium">{entry.responsavel || entry.usuario_nome}</span>
                    </div>
                    <p className="text-gray-700">{entry.conteudo}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={onSave} 
            disabled={!diarioContent.trim() || saving}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog de Detalhes da Fase
export const DialogDetalhesFase = ({
  open,
  onClose,
  selectedFase,
  setSelectedFase,
  saving,
  onSave
}) => {
  if (!selectedFase) return null;

  const handleToggleTodasEtapas = () => {
    const todasConcluidas = selectedFase.etapas?.every(e => e.concluida);
    const etapasAtualizadas = selectedFase.etapas?.map(e => ({ ...e, concluida: !todasConcluidas })) || [];
    const progresso = todasConcluidas ? 0 : 100;
    setSelectedFase({ ...selectedFase, etapas: etapasAtualizadas, progresso });
  };

  const handleToggleEtapa = (idx) => {
    const etapasAtualizadas = [...(selectedFase.etapas || [])];
    etapasAtualizadas[idx] = { ...etapasAtualizadas[idx], concluida: !etapasAtualizadas[idx].concluida };
    const concluidas = etapasAtualizadas.filter(e => e.concluida).length;
    const progresso = Math.round((concluidas / etapasAtualizadas.length) * 100);
    setSelectedFase({ ...selectedFase, etapas: etapasAtualizadas, progresso });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {selectedFase?.fase_nome}
            <HelpTooltip 
              title="Detalhes da Fase"
              description="Visualize e edite os detalhes desta fase de produção."
              connections={['Cronograma', 'Diário de Produção']}
              iconSize={16}
            />
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Descrição da fase */}
          {selectedFase.fase_descricao && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-xs text-blue-600">Descrição da Fase</Label>
              <p className="text-sm text-gray-700 mt-1">{selectedFase.fase_descricao}</p>
            </div>
          )}
          
          {/* Datas Planejadas */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <Label className="text-xs text-gray-500 mb-2 block">Datas Planejadas</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Previsão Início</Label>
                <Input
                  type="date"
                  value={selectedFase.data_previsao_inicio || selectedFase.data_inicio || ''}
                  onChange={(e) => setSelectedFase({ 
                    ...selectedFase, 
                    data_previsao_inicio: e.target.value,
                    data_inicio: e.target.value 
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Previsão Fim</Label>
                <Input
                  type="date"
                  value={selectedFase.data_previsao_fim || selectedFase.data_fim || ''}
                  onChange={(e) => setSelectedFase({ 
                    ...selectedFase, 
                    data_previsao_fim: e.target.value,
                    data_fim: e.target.value 
                  })}
                />
              </div>
            </div>
          </div>
          
          {/* Datas Reais */}
          <div className="p-3 bg-green-50 rounded-lg">
            <Label className="text-xs text-green-600 mb-2 block">Execução Real</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Início Real</Label>
                <Input
                  type="date"
                  value={selectedFase.data_inicio_real?.split('T')[0] || ''}
                  onChange={(e) => setSelectedFase({ 
                    ...selectedFase, 
                    data_inicio_real: e.target.value ? new Date(e.target.value).toISOString() : null
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fim Real</Label>
                <Input
                  type="date"
                  value={selectedFase.data_fim_real?.split('T')[0] || ''}
                  onChange={(e) => setSelectedFase({ 
                    ...selectedFase, 
                    data_fim_real: e.target.value ? new Date(e.target.value).toISOString() : null
                  })}
                />
              </div>
            </div>
          </div>
          
          {/* Status e Progresso */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={selectedFase.status || 'planejado'}
                onValueChange={(value) => setSelectedFase({ ...selectedFase, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Progresso</Label>
              <div className="flex items-center gap-2">
                <Progress value={selectedFase.progresso || 0} className="flex-1" />
                <span className="text-sm font-medium">{selectedFase.progresso || 0}%</span>
              </div>
            </div>
          </div>
          
          {/* Etapas */}
          {selectedFase.etapas?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Etapas ({selectedFase.etapas.filter(e => e.concluida).length}/{selectedFase.etapas.length})</Label>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleToggleTodasEtapas}
                >
                  {selectedFase.etapas.every(e => e.concluida) ? 'Desmarcar' : 'Marcar'} Todas
                </Button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedFase.etapas.map((etapa, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded ${
                      etapa.concluida ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    <Checkbox
                      checked={etapa.concluida}
                      onCheckedChange={() => handleToggleEtapa(idx)}
                    />
                    <span className={etapa.concluida ? 'line-through text-gray-500' : ''}>
                      {etapa.nome}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Observações */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={selectedFase.observacoes || ''}
              onChange={(e) => setSelectedFase({ ...selectedFase, observacoes: e.target.value })}
              placeholder="Notas, observações, pendências..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog de Conclusão com Foto
export const DialogConclusao = ({
  open,
  onClose,
  faseConcluindo,
  fotoConclusao,
  setFotoConclusao,
  fotoPreview,
  setFotoPreview,
  observacaoConclusao,
  setObservacaoConclusao,
  saving,
  onConfirmar
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFotoConclusao(file);
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFoto = () => {
    setFotoConclusao(null);
    setFotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Concluir Fase: {faseConcluindo?.fase_nome}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <Camera className="h-4 w-4" />
              <strong>Foto obrigatória:</strong> Anexe uma foto comprovando a conclusão da fase.
            </p>
          </div>
          
          {/* Upload de foto */}
          <div className="space-y-2">
            <Label>Foto de Conclusão *</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="foto-conclusao-upload"
            />
            
            {fotoPreview ? (
              <div className="relative">
                <img 
                  src={fotoPreview} 
                  alt="Preview" 
                  className="w-full h-48 object-cover rounded-lg border"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute top-2 right-2"
                  onClick={handleRemoveFoto}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label 
                htmlFor="foto-conclusao-upload"
                className="block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Toque para selecionar uma foto</p>
                <p className="text-xs text-gray-400">ou arraste e solte aqui</p>
              </label>
            )}
          </div>
          
          {/* Observações */}
          <div className="space-y-2">
            <Label>Observações (opcional)</Label>
            <Textarea
              value={observacaoConclusao}
              onChange={(e) => setObservacaoConclusao(e.target.value)}
              placeholder="Detalhes sobre a conclusão, problemas encontrados..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={onConfirmar}
            disabled={!fotoConclusao || saving}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Confirmar Conclusão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
